<template>
  <div :class="variant === 'modal' ? 'mt-3.5 px-6' : 'px-4 pt-5'">
    <MatchCompareCard
      v-bind="works"
      :actions="{ onView: handleView, onDetails: checker.openDetails }"
    />

    <MatchLevelPanel :panel="panel" />

    <section
      v-if="finding.audioCompositionEvidence || finding.validationPending"
      class="mt-3.5 rounded-xl border border-gray-200 p-4 text-[14px] leading-5 text-gray-800"
      aria-label="Detector evidence"
    >
      <Pill v-if="finding.validationPending" variant="warning">
        Validation pending
      </Pill>
      <template v-if="finding.audioCompositionEvidence">
        <p class="mt-2 font-medium">
          {{ finding.strict ? 'Strict cover match' : 'Cover review' }}
        </p>
        <p>Cover scores are native method values, not percentages.</p>
        <details class="mt-2 text-gray-500">
          <summary class="cursor-pointer">Technical evidence</summary>
          <p>
            Native policy score:
            {{ finding.audioCompositionEvidence.policyScore }}
          </p>
          <p v-if="finding.audioCompositionEvidence.policyMargin != null">
            Policy margin: {{ finding.audioCompositionEvidence.policyMargin }}
          </p>
          <p>Raw score: {{ finding.audioCompositionEvidence.rawScore }}</p>
          <p
            v-if="
              finding.audioCompositionEvidence.referenceStartSeconds != null
            "
          >
            Reference start:
            {{ finding.audioCompositionEvidence.referenceStartSeconds }} seconds
          </p>
          <p v-if="finding.audioCompositionEvidence.supporting.length">
            {{
              finding.audioCompositionEvidence.supportKind ===
              'live_deadline_seconds'
                ? 'Capture deadlines (seconds)'
                : 'File window indexes'
            }}:
            {{ finding.audioCompositionEvidence.supporting.join(', ') }}
          </p>
          <p>
            Repeated evidence:
            {{ finding.audioCompositionEvidence.confirmed ? 'Yes' : 'No' }}
          </p>
          <p v-if="finding.audioCompositionEvidence.exactLaneMatch">
            Recording match also found.
          </p>
        </details>
      </template>
    </section>

    <Button
      variant="primary"
      size="lg"
      class="mt-[34px] h-10! w-full"
      :class="variant === 'sheet' && 'mb-[env(safe-area-inset-bottom)]'"
      :disabled="
        checker.credits.value === undefined ||
        !results.media ||
        !finding.fileDataId
      "
      @click="checker.openConfirm"
    >
      Create report
    </Button>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Button, Pill } from '@dacr/library'

import { levelPanel } from '../match/levels'
import MatchCompareCard from '../match/MatchCompareCard.vue'
import MatchLevelPanel from '../match/MatchLevelPanel.vue'
import { findingLevel, type Finding } from './findings'
import { checkerWorks } from './material'
import type { CheckerResults } from './results'
import { useChecker } from './useChecker'

const props = defineProps<{
  variant: 'modal' | 'sheet'
  finding: Finding
  results: CheckerResults
}>()

const checker = useChecker()

const works = computed(() => checkerWorks(props.finding, props.results))
const panel = computed(() => {
  const level = findingLevel(props.finding)
  const result = levelPanel(level)
  return {
    ...result,
    label: `${level.charAt(0)}${level.slice(1).toLowerCase()} risk`,
    body:
      props.finding.scorePercent == null
        ? 'This result has a detector risk level, but no overall similarity percentage. Review the detector evidence in a full report.'
        : result.body,
  }
})

const handleView = computed(() => {
  const copyrightId = props.finding.copyrightId
  return copyrightId ? () => checker.openFile(copyrightId) : undefined
})
</script>
